// Generador del .xlsx de pago masivo del Banco Santander (Chile): IDÉNTICO al template
// que importa el banco, con una fila por transferencia a repartidor. El orden/textos de
// los encabezados y —sobre todo— el TIPO de cada celda (número vs. texto) son parte del
// contrato: un RUT con dígito verificador 'K' o una cuenta con cero inicial DEBEN ir como
// texto para no perder datos.
import { utils, writeFile } from 'xlsx';

export const code = '037';
export const label = 'Banco Santander';

// Encabezados EXACTOS (13 columnas A..M) del template Santander. Los `\n` son saltos de
// línea literales dentro de la celda; se muestran envueltos gracias a wrapText.
export const BANK_SHEET_HEADERS = [
  'Cuenta origen\n(obligatorio)', // A
  'Moneda origen\n(obligatorio)', // B
  'Cuenta destino\n(obligatorio)', // C
  'Moneda destino\n(obligatorio)', // D
  'Código banco destino\n(obligatorio solo si banco destino no es Santander)', // E
  'RUT beneficiario\n(obligatorio solo si banco destino no es Santander)', // F
  'Nombre beneficiario\n(obligatorio solo si banco destino no es Santander)', // G
  'Monto transferencia\n(obligatorio)', // H
  'Glosa personalizada transferencia\n(opcional)', // I
  'Correo beneficiario\n(opcional)', // J
  'Mensaje correo beneficiario\n(opcional)', // K
  'Glosa cartola originador\n(opcional)', // L
  'Glosa cartola beneficiario\n(opcional, solo aplica si cuenta destino es Santander)', // M
];

// Anchos de columna (aprox, en caracteres) para que el archivo se lea cómodo.
const COL_WIDTHS = [16, 14, 16, 14, 14, 16, 26, 16, 26, 26, 26, 26, 30];

const isAllDigits = (s) => /^\d+$/.test(s);
// Cero a la izquierda con más de un dígito ("0123"); un "0" suelto no cuenta.
const hasLeadingZero = (s) => s.length > 1 && s[0] === '0';

// Cuentas y códigos de banco: número solo si son puros dígitos, sin cero inicial y no
// tan largos como para perder precisión (>15 dígitos supera el entero seguro de JS y de
// Excel). En cualquier otro caso → texto, para conservar todos los dígitos tal cual.
const accountCell = (raw) => {
  const s = String(raw ?? '').trim();
  if (s === '') return '';
  if (isAllDigits(s) && !hasLeadingZero(s) && s.length <= 15) return Number(s);
  return s;
};

// RUT beneficiario: número si es puramente dígitos; si trae dígito verificador 'K' (o
// cualquier no-dígito, o cero inicial) → texto para no perderlo.
const rutCell = (raw) => {
  const s = String(raw ?? '').trim();
  if (s === '') return '';
  if (isAllDigits(s) && !hasLeadingZero(s) && s.length <= 15) return Number(s);
  return s;
};

// Monto: entero (los pesos no llevan decimales). Siempre número.
const amountCell = (raw) => Math.round(Number(raw) || 0);

const textCell = (raw) => String(raw ?? '');

// Convierte una fila del API en el arreglo de 13 celdas (A..M) con el tipo correcto.
// `origin` se repite en cada fila (columnas A y B) porque el banco lo exige por transfer.
// El template guarda la Cuenta DESTINO como TEXTO (t:'s') aunque parezca numérica —lo
// seguro para números de cuenta (ceros a la izquierda / largos)—, mientras que la Cuenta
// ORIGEN va como número. Replicamos esa asimetría para que el archivo sea idéntico. La
// glosa (columna I) va con el texto "Factura 14"/"Boleta 15" calculado por el backend; las
// demás columnas opcionales (K/L/M) quedan AUSENTES (null), como en el template.
const rowToCells = (origin, row) => [
  accountCell(origin?.account), // A Cuenta origen (número, como el template)
  textCell(origin?.currency), // B Moneda origen
  textCell(row?.destAccount), // C Cuenta destino (TEXTO, como el template)
  textCell(row?.destCurrency), // D Moneda destino
  accountCell(row?.bankCode), // E Código banco destino
  rutCell(row?.beneficiaryTaxId), // F RUT beneficiario
  textCell(row?.beneficiaryName), // G Nombre beneficiario
  amountCell(row?.amount), // H Monto transferencia
  textCell(row?.glosa), // I Glosa personalizada transferencia
  textCell(row?.beneficiaryEmail), // J Correo beneficiario
  null, // K Mensaje correo beneficiario (opcional, ausente)
  null, // L Glosa cartola originador (opcional, ausente)
  null, // M Glosa cartola beneficiario (opcional, ausente)
];

// Construye el workbook (hoja "Hoja1"). Puro y testeable: recibe { origin, rows } y
// devuelve el workbook de SheetJS. `rows` ya viene filtrado por el backend (monto>0 +
// datos bancarios completos); acá solo se le da forma y tipo de celda.
export function buildWorkbook({ origin, rows = [] }) {
  const aoa = [BANK_SHEET_HEADERS, ...rows.map((r) => rowToCells(origin, r))];
  const ws = utils.aoa_to_sheet(aoa);

  ws['!cols'] = COL_WIDTHS.map((wch) => ({ wch }));
  // Fila de encabezados más alta para que el salto de línea se vea envuelto.
  ws['!rows'] = [{ hpt: 30 }];

  // Estilo de los encabezados: envolver texto (para los `\n`). En el build community de
  // SheetJS los estilos no se escriben, pero el `\n` queda igual en el valor (que es lo
  // que el banco importa); con un build con estilos, además se ve envuelto.
  for (let c = 0; c < BANK_SHEET_HEADERS.length; c += 1) {
    const addr = utils.encode_cell({ r: 0, c });
    if (ws[addr]) {
      ws[addr].s = { alignment: { wrapText: true, vertical: 'top' } };
    }
  }

  const wb = utils.book_new();
  utils.book_append_sheet(wb, ws, 'Hoja1');
  return wb;
}

// Nombre de archivo sugerido: planilla-pago-banco_<from>_<to>.xlsx
export const filename = (from, to) =>
  `planilla-pago-banco_${from}_${to}.xlsx`;

// Construye y dispara la descarga del .xlsx en el navegador.
export function download({ origin, rows, filename: name }) {
  const wb = buildWorkbook({ origin, rows });
  writeFile(wb, name || filename('', ''), { cellStyles: true });
}

// ── Aliases legacy: `bankSheet.js` y cualquier consumidor antiguo siguen funcionando ──
export const buildBankSheetWorkbook = buildWorkbook;
export const bankSheetFilename = filename;
export const downloadBankSheet = download;
